import { notifyError } from "@/lib/notify-error";
import { useRef, useState, useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { Plus } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { fromDateInputValue } from "@/lib/date-input";
import { cn } from "@/lib/utils";
import { DatePicker } from "@/components/ui/date-picker";

interface SelectOption {
  value: string;
  label: string;
}

interface NamedItem {
  _id: string;
  name: string;
}

interface TeamMemberItem extends NamedItem {
  email: string;
}

const submittalControlClassName = "relative h-7 w-[150px] truncate gap-1.5 px-2 py-1 text-xs [&_svg]:!size-3";

function OptionSelectDropdown({
  value,
  onChange,
  options,
  className,
  placeholder,
  disabled,
}: {
  value?: string;
  onChange: (value: string) => void;
  options: readonly SelectOption[];
  className?: string;
  placeholder: string;
  disabled?: boolean;
}) {
  const [key, setKey] = useState(0);

  return (
    <Select
      key={value !== undefined ? undefined : key}
      value={value}
      onValueChange={(v) => {
        onChange(v);
        if (value === undefined) setKey((k) => k + 1);
      }}
      disabled={disabled}
    >
      <SelectTrigger size="sm" className={cn(submittalControlClassName, className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="z-[160]" position="popper" sideOffset={4} align="start" collisionPadding={8}>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function CustomNumberDropdown({
  value,
  onChange,
  options,
  className,
  placeholder,
  min,
  disabled,
}: {
  value?: string;
  onChange: (value: string) => void;
  options: readonly SelectOption[];
  className?: string;
  placeholder: string;
  min: number;
  disabled?: boolean;
}) {
  const [isCustom, setIsCustom] = useState(false);
  const [customValue, setCustomValue] = useState("");

  useEffect(() => {
    if (disabled) {
      setIsCustom(false);
      setCustomValue("");
    }
  }, [disabled]);

  const submitCustomValue = () => {
    const next = customValue.trim();
    if (!next || parseInt(next) < min) return;
    onChange(next);
    setIsCustom(false);
    setCustomValue("");
  };

  if (isCustom) {
    return (
      <div className={cn("flex items-center gap-1", className)}>
        <Input
          type="number"
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
          placeholder="Days"
          className="w-[80px]"
          min={min}
          disabled={disabled}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submitCustomValue();
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setIsCustom(false);
              setCustomValue("");
            }
          }}
        />
        <span className="text-sm text-muted-foreground">days</span>
        <Button variant="ghost" size="sm" onClick={submitCustomValue} disabled={disabled} type="button">
          OK
        </Button>
      </div>
    );
  }

  return (
    <Select
      value={value}
      onValueChange={(v) => {
        if (v === "custom") {
          setIsCustom(true);
          return;
        }
        onChange(v);
      }}
      disabled={disabled}
    >
      <SelectTrigger size="sm" className={cn(submittalControlClassName, className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="z-[160]" position="popper" sideOffset={4} align="start" collisionPadding={8}>
        {value && !options.some((o) => o.value === value) && (
          <SelectItem value={value}>{value} days</SelectItem>
        )}
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
        <SelectItem value="custom">Custom...</SelectItem>
      </SelectContent>
    </Select>
  );
}

function CreatableSelectDropdown({
  items,
  value,
  onChange,
  onAddNew,
  className,
  placeholder,
  newItemPlaceholder,
  addLabel,
  disabled,
  addMode = "inline",
  addDialogTitle = "Add New Item",
  addDialogDescription = "Enter a name for the new item.",
}: {
  items: NamedItem[];
  value?: string;
  onChange: (value: string) => void;
  onAddNew: (name: string) => Promise<void>;
  className?: string;
  placeholder: string;
  newItemPlaceholder: string;
  addLabel: ReactNode;
  disabled?: boolean;
  addMode?: "inline" | "dialog";
  addDialogTitle?: string;
  addDialogDescription?: string;
}) {
  const [showAddNew, setShowAddNew] = useState(false);
  const [showAddNewDialog, setShowAddNewDialog] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (disabled) {
      setShowAddNew(false);
      setShowAddNewDialog(false);
      setNewItemName("");
      cancelledRef.current = true;
    }
  }, [disabled]);

  const handleAddDialogOpenChange = (open: boolean) => {
    setShowAddNewDialog(open);
    if (!open) {
      cancelledRef.current = true;
      setNewItemName("");
    }
  };

  const handleAddNew = async () => {
    const nextName = newItemName.trim();
    if (!nextName || isAdding) return;
    cancelledRef.current = false;
    setIsAdding(true);
    try {
      await onAddNew(nextName);
      if (cancelledRef.current) return;
      onChange(nextName);
      setNewItemName("");
      setShowAddNew(false);
      setShowAddNewDialog(false);
    } catch (error) {
      console.error("Failed to add item:", error);
      notifyError(error,
        error instanceof Error
          ? `Failed to add item: ${error.message}`
          : "Failed to add item"
      );
    } finally {
      setIsAdding(false);
    }
  };

  if (showAddNew) {
    return (
      <div className={cn("flex items-center gap-1", className)}>
        <Input
          value={newItemName}
          onChange={(e) => setNewItemName(e.target.value)}
          placeholder={newItemPlaceholder}
          className="w-[160px]"
          disabled={disabled || isAdding}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !isAdding) {
              e.preventDefault();
              void handleAddNew();
            }
            if (e.key === "Escape") {
              e.preventDefault();
              cancelledRef.current = true;
              setShowAddNew(false);
              setNewItemName("");
            }
          }}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void handleAddNew()}
          disabled={disabled || isAdding || !newItemName.trim()}
          type="button"
        >
          Add
        </Button>
      </div>
    );
  }

  return (
    <>
      <Select
        value={value}
        onValueChange={(v) => {
          if (v === "__add_new__") {
            if (addMode === "dialog") {
              setShowAddNewDialog(true);
            } else {
              setShowAddNew(true);
            }
            return;
          }
          onChange(v);
        }}
        disabled={disabled}
      >
        <SelectTrigger size="sm" className={cn(submittalControlClassName, className)}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="z-[160]" position="popper" sideOffset={4} align="start" collisionPadding={8}>
          {items.map((item) => (
            <SelectItem key={item._id} value={item.name}>
              {item.name}
            </SelectItem>
          ))}
          <SelectItem value="__add_new__">{addLabel}</SelectItem>
        </SelectContent>
      </Select>

      {addMode === "dialog" && (
        <Dialog open={showAddNewDialog} onOpenChange={handleAddDialogOpenChange}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{addDialogTitle}</DialogTitle>
              <DialogDescription>{addDialogDescription}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-4">
              <Label htmlFor="new-creatable-item">Name</Label>
              <Input
                id="new-creatable-item"
                value={newItemName}
                onChange={(event) => setNewItemName(event.target.value)}
                placeholder={newItemPlaceholder}
                disabled={disabled || isAdding}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && newItemName.trim() && !isAdding) {
                    event.preventDefault();
                    void handleAddNew();
                  }
                }}
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => handleAddDialogOpenChange(false)}
                disabled={isAdding}
              >
                Cancel
              </Button>
              <Button
                onClick={() => void handleAddNew()}
                disabled={isAdding || !newItemName.trim()}
              >
                Add
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

const SUBMITTAL_STATUS_OPTIONS = [
  { value: "Open", label: "Open" },
  { value: "Draft", label: "Draft" },
  { value: "Submitted", label: "Submitted" },
  { value: "Approved", label: "Approved" },
  { value: "Approved As Noted", label: "Approved As Noted" },
  { value: "For Record", label: "For Record" },
  { value: "Closed", label: "Closed" },
] as const;

const SUBMITTAL_TYPE_OPTIONS = [
  { value: "Product Information", label: "Product Information" },
  { value: "Shop Drawing", label: "Shop Drawing" },
  { value: "Specification", label: "Specification" },
  { value: "Sample", label: "Sample" },
  { value: "Mock-Up", label: "Mock-Up" },
  { value: "Document", label: "Document" },
  { value: "Closeout", label: "Closeout" },
  { value: "As-Built", label: "As-Built" },
] as const;

const REVIEW_TIME_OPTIONS = [
  { value: "10", label: "10 days" },
  { value: "5", label: "5 days" },
  { value: "15", label: "15 days" },
] as const;

const INTERNAL_REVIEW_OPTIONS = [
  { value: "0", label: "0 days" },
  { value: "1", label: "1 day" },
  { value: "2", label: "2 days" },
  { value: "3", label: "3 days" },
  { value: "5", label: "5 days" },
] as const;

const DURATION_OPTIONS = [
  { value: "10", label: "10 days" },
  { value: "15", label: "15 days" },
  { value: "30", label: "30 days" },
  { value: "60", label: "60 days" },
  { value: "90", label: "90 days" },
] as const;

interface BaseDropdownProps {
  value?: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

export function SubmittalStatusDropdown({
  value,
  onChange,
  className,
  placeholder = "Select status",
  disabled,
}: BaseDropdownProps) {
  return (
    <OptionSelectDropdown
      value={value}
      onChange={onChange}
      options={SUBMITTAL_STATUS_OPTIONS}
      className={className}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}

export function SubmittalTypeDropdown({
  value,
  onChange,
  className,
  placeholder = "Select type",
  disabled,
}: BaseDropdownProps) {
  return (
    <OptionSelectDropdown
      value={value}
      onChange={onChange}
      options={SUBMITTAL_TYPE_OPTIONS}
      className={className}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}

export function ReviewTimeDropdown({
  value,
  onChange,
  className,
  placeholder = "10 days",
  disabled,
}: BaseDropdownProps) {
  return (
    <CustomNumberDropdown
      value={value}
      onChange={onChange}
      options={REVIEW_TIME_OPTIONS}
      className={className}
      placeholder={placeholder}
      min={1}
      disabled={disabled}
    />
  );
}

export function InternalReviewDropdown({
  value,
  onChange,
  className,
  disabled,
}: BaseDropdownProps) {
  return (
    <CustomNumberDropdown
      value={value ?? "0"}
      onChange={onChange}
      options={INTERNAL_REVIEW_OPTIONS}
      className={className}
      placeholder="0 days"
      min={0}
      disabled={disabled}
    />
  );
}

interface DurationDropdownProps extends BaseDropdownProps {
  items?: NamedItem[];
  projectId?: string;
  onAddNew?: (name: string) => Promise<void>;
}

export function DurationDropdown({
  value,
  onChange,
  className,
  placeholder = "Days",
  disabled,
  items = [],
  onAddNew,
}: DurationDropdownProps) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const allOptions = useMemo(() => {
    const base = DURATION_OPTIONS.map((o) => ({ ...o }));
    const custom = items
      .filter((item) => !base.some((o) => o.value === item.name))
      .map((item) => ({ value: item.name, label: `${item.name} days` }));
    return [...base, ...custom].sort((a, b) => Number(a.value) - Number(b.value));
  }, [items]);

  const handleAddNew = async () => {
    const next = newValue.trim();
    if (!next || isNaN(Number(next)) || Number(next) < 0 || isAdding) return;
    setIsAdding(true);
    try {
      await onAddNew?.(next);
      onChange(next);
      setNewValue("");
      setShowAddDialog(false);
    } catch (error) {
      console.error("Failed to add custom duration:", error);
      notifyError(error, "Failed to add custom duration");
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <>
      <Select
        value={value}
        onValueChange={(v) => {
          if (v === "__add_new__") {
            setShowAddDialog(true);
            return;
          }
          onChange(v);
        }}
        disabled={disabled}
      >
        <SelectTrigger size="sm" className={cn(submittalControlClassName, className)}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="z-[160]" position="popper" sideOffset={4} align="start" collisionPadding={8}>
          {value && !allOptions.some((o) => o.value === value) && (
            <SelectItem value={value}>{value} days</SelectItem>
          )}
          {allOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
          {onAddNew && (
            <SelectItem value="__add_new__">
              <div className="flex items-center gap-2">
                <Plus className="size-4" />
                Custom...
              </div>
            </SelectItem>
          )}
        </SelectContent>
      </Select>

      {onAddNew && (
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Custom Duration</DialogTitle>
              <DialogDescription>Enter a custom number of days.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-4">
              <Label htmlFor="new-duration">Days</Label>
              <Input
                id="new-duration"
                type="number"
                value={newValue}
                onChange={(event) => setNewValue(event.target.value)}
                placeholder="e.g. 45"
                disabled={disabled || isAdding}
                min={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && newValue.trim() && !isAdding) {
                    event.preventDefault();
                    void handleAddNew();
                  }
                }}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)} disabled={isAdding}>
                Cancel
              </Button>
              <Button onClick={() => void handleAddNew()} disabled={isAdding || !newValue.trim() || isNaN(Number(newValue))}>
                Add
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export function DateDropdown({
  value,
  onChange,
  className,
  disabled,
}: BaseDropdownProps) {
  // Stored as MM/dd/yyyy here; the shared picker works in ISO, so convert on the way out.
  return (
    <DatePicker
      value={value}
      onChange={(iso) => onChange(iso ? fromDateInputValue(iso) : "")}
      className={cn(submittalControlClassName, className)}
      disabled={disabled}
    />
  );
}

interface TeamMemberDropdownProps extends BaseDropdownProps {
  members: TeamMemberItem[];
}

export function TeamMemberDropdown({
  members,
  value,
  onChange,
  className,
  placeholder = "Select member",
  disabled,
}: TeamMemberDropdownProps) {
  return (
    <OptionSelectDropdown
      value={value}
      onChange={onChange}
      // Procore matches users by email on import, so the cell stores the email.
      options={members.map((member) => ({ value: member.email, label: member.name }))}
      className={className}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}

interface LocationDropdownProps extends BaseDropdownProps {
  locations: NamedItem[];
  onAddNew: (name: string) => Promise<void>;
}

export function LocationDropdown({
  locations,
  value,
  onChange,
  onAddNew,
  className,
  placeholder = "Select location",
  disabled,
}: LocationDropdownProps) {
  return (
    <CreatableSelectDropdown
      items={locations}
      value={value}
      onChange={onChange}
      onAddNew={onAddNew}
      className={className}
      placeholder={placeholder}
      newItemPlaceholder="New location"
      disabled={disabled}
      addMode="dialog"
      addDialogTitle="Add Custom Location"
      addDialogDescription="Enter a custom location name to use in the submittal table."
      addLabel={
        <div className="flex items-center gap-2">
          <Plus className="size-4" />
          Add new location
        </div>
      }
    />
  );
}

interface ProjectItemDropdownProps extends BaseDropdownProps {
  items: NamedItem[];
  itemLabel: string;
  onAddNew: (name: string) => Promise<void>;
}

export function ProjectItemDropdown({
  items,
  itemLabel,
  value,
  onChange,
  onAddNew,
  className,
  placeholder,
  disabled,
}: ProjectItemDropdownProps) {
  const normalizedLabel = itemLabel.trim() || "item";
  return (
    <CreatableSelectDropdown
      items={items}
      value={value}
      onChange={onChange}
      onAddNew={onAddNew}
      className={className}
      placeholder={placeholder ?? `Select ${normalizedLabel.toLowerCase()}`}
      newItemPlaceholder={`New ${normalizedLabel.toLowerCase()}`}
      disabled={disabled}
      addMode="dialog"
      addDialogTitle={`Add ${normalizedLabel}`}
      addDialogDescription={`Enter a ${normalizedLabel.toLowerCase()} to reuse on this project.`}
      addLabel={
        <div className="flex items-center gap-2">
          <Plus className="size-4" />
          Add new {normalizedLabel.toLowerCase()}
        </div>
      }
    />
  );
}

interface ContractorDropdownProps extends BaseDropdownProps {
  contractors: NamedItem[];
  onAddNew: (name: string) => Promise<void>;
}

export function ContractorDropdown({
  contractors,
  value,
  onChange,
  onAddNew,
  className,
  placeholder = "Select contractor",
  disabled,
}: ContractorDropdownProps) {
  return (
    <CreatableSelectDropdown
      items={contractors}
      value={value}
      onChange={onChange}
      onAddNew={onAddNew}
      className={className}
      placeholder={placeholder}
      newItemPlaceholder="New contractor"
      disabled={disabled}
      addLabel={
        <div className="flex items-center gap-2">
          <Plus className="size-4" />
          Add new contractor
        </div>
      }
    />
  );
}
